import { dayjs } from '@/shared/lib/datetime'
import { TFunction, TranslationKey } from '@/shared/lib/i18n'

type DurationDisplayPartUnit = 'days' | 'hours' | 'minutes'

const ORDERED_PART_UNITS = ['days', 'hours', 'minutes'] as const satisfies DurationDisplayPartUnit[]

const TRANSLATION_KEY_BY_UNIT = {
  days: 'dateTime.daysWithCount',
  hours: 'dateTime.hoursWithCount',
  minutes: 'dateTime.minutesWithCount',
} as const satisfies Record<DurationDisplayPartUnit, TranslationKey>

export type DurationPart = {
  unit: DurationDisplayPartUnit
  value: number
}

type DurationPartFormatRule = {
  hideUnit?: true
}
type DurationPartFormatRuleByUnit = {
  [U in DurationDisplayPartUnit]?: DurationPartFormatRule
}

const DEFAULT_PARTS_FROMAT_RULES = Object.freeze({}) satisfies DurationPartFormatRuleByUnit

export class SingleDurationDisplay {
  #parts: DurationPart[] = []

  constructor(durationMinutes: number) {
    const dayjsDuration = dayjs.duration(Math.round(durationMinutes), 'minutes')

    ORDERED_PART_UNITS.forEach((partUnit) => {
      const durationPartValue = dayjsDuration[partUnit]()
      if (!durationPartValue) return

      this.#parts.push({
        unit: partUnit,
        value: durationPartValue,
      })
    })
  }

  getParts() {
    return this.#parts
  }

  format({ t, rules = DEFAULT_PARTS_FROMAT_RULES }: { t: TFunction; rules?: DurationPartFormatRuleByUnit }): string {
    return this.#parts
      .map((part) => {
        const shouldHideUnit = !!rules[part.unit]?.hideUnit
        if (shouldHideUnit) return String(part.value)

        const unitTranslationKey = TRANSLATION_KEY_BY_UNIT[part.unit]
        return t(unitTranslationKey, { count: part.value })
      })
      .join(' ')
  }
}

export type DurationRangeFormatRules = {
  start: DurationPartFormatRuleByUnit
  end: DurationPartFormatRuleByUnit
}

const DEFAULT_RANGE_FORMAT_RULES = Object.freeze({
  start: DEFAULT_PARTS_FROMAT_RULES,
  end: DEFAULT_PARTS_FROMAT_RULES,
}) satisfies DurationRangeFormatRules

export class DurationRangeDisplay {
  #startParts: SingleDurationDisplay
  #endParts: SingleDurationDisplay
  #formatRules: DurationRangeFormatRules

  constructor([rangeStartMinutes, rangeEndMinutes]: [number, number]) {
    this.#startParts = new SingleDurationDisplay(rangeStartMinutes)
    this.#endParts = new SingleDurationDisplay(rangeEndMinutes)
    this.#formatRules = getDurationRangeFormatRules(this.#startParts.getParts(), this.#endParts.getParts())
  }

  format({ t }: { t: TFunction }) {
    const formattedStart = this.#startParts.format({ t, rules: this.#formatRules.start })
    const formattedEnd = this.#endParts.format({ t, rules: this.#formatRules.end })
    return `${formattedStart} - ${formattedEnd}`
  }
}

export const getDurationRangeFormatRules = (
  startPartList: DurationPart[],
  endPartList: DurationPart[],
): DurationRangeFormatRules => {
  const bothPartListsContainOnlyOneElement = startPartList.length === 1 && endPartList.length === 1
  if (!bothPartListsContainOnlyOneElement) return DEFAULT_RANGE_FORMAT_RULES

  const theOnlyStartPart = startPartList[0]
  const theOnlyEndPart = endPartList[0]

  const bothPartsHaveTheSameUnit = theOnlyStartPart.unit === theOnlyEndPart.unit
  if (!bothPartsHaveTheSameUnit) return DEFAULT_RANGE_FORMAT_RULES

  return {
    start: {
      [theOnlyStartPart.unit]: {
        hideUnit: true,
      },
    },
    end: DEFAULT_PARTS_FROMAT_RULES,
  }
}
